import type { SelectOption } from '@/shared/ui/select'

export const REPORT_VIEW_TYPES = ['table', 'chart', 'card', 'cohort'] as const
export type ReportViewType = (typeof REPORT_VIEW_TYPES)[number]

/** Closed set backed by an implemented module-08 option builder. Backend keys stay open strings. */
export const RENDERABLE_CHART_TYPES = [
  'line',
  'area',
  'stacked_area',
  'multi_line',
  'compare_column',
  'compare_bar',
  'column',
  'bar',
  'stacked_column',
  'stacked_bar',
  'pie',
  'multi_pie',
  'donut',
  'scatter',
  'bubble',
  'heatmap',
  'treemap',
  'sunburst',
  'funnel',
  'waterfall',
  'radar',
  'gauge',
  'sankey',
  'packed_bubble',
  'lollipop',
  'combination',
  'multi_metric',
  'dumbbell',
  'bullet',
  'diverging_bar',
] as const
export type ChartType = (typeof RENDERABLE_CHART_TYPES)[number]

export const CHART_FIELD_TYPES = [
  'metric',
  'dimension',
  'time_dimension',
  'target',
  'value',
] as const
export type ChartFieldType = (typeof CHART_FIELD_TYPES)[number]

/** Validated projection of one backend `field_roles` entry. Labels/descriptions stay backend-owned
 *  copy; selection behavior remains frontend-owned and contains field ids only. */
export interface ChartFieldRole {
  key: string
  fieldType: ChartFieldType
  label: string
  description: string | null
  required: boolean
  multiple: boolean
  /** Normalized to 0/1 for single-select roles. */
  min: number
  /** Always finite at the frontend picker boundary. */
  max: number
}

export type ChartFieldCandidate = SelectOption<string>

export interface ChartFieldRoleSources {
  dimensions: readonly ChartFieldCandidate[]
  metrics: readonly ChartFieldCandidate[]
  timeDimensions?: readonly ChartFieldCandidate[]
  targets?: readonly ChartFieldCandidate[]
  values?: readonly ChartFieldCandidate[]
}

/** Durable field assignment. A single-select role is represented as a zero/one-item array too, so
 *  parsing, de-duplication and persistence use one stable shape. */
export type ChartFieldRoleSelection = Record<string, string[]>

export interface ChartFieldRoleSlot {
  role: ChartFieldRole
  options: ChartFieldCandidate[]
  value: string[]
  valid: boolean
}

export interface ChartFieldRoleResolution {
  slots: ChartFieldRoleSlot[]
  selection: ChartFieldRoleSelection
  valid: boolean
}

export interface ChartFieldRoleFields {
  dimensions: string[]
  metrics: string[]
  timeDimensions: string[]
  targets: string[]
  values: string[]
}

export interface VisualizationTypeDefinition {
  kind: 'chart' | 'table'
  type: string
  label: string | null
  description: string | null
  minDimensions: number
  maxDimensions: number | null
  minMetrics: number
  maxMetrics: number | null
  requiresTimeDimension: boolean
  defaultLimit: number | null
  maxLimit: number | null
  maxTimeDimensions: number
  minDataPoints: number
  requiresComparison: boolean
  requiresHierarchy: boolean
  requiresPairs: boolean
  isWidget: boolean
  /** Series cap for grouped/time-bucketed renderers; `null` means the backend sets none. */
  maxSeries: number | null
  /** Pane cap for small-multiple renderers (one pie per metric, one panel per metric). */
  maxSmallMultiples: number | null
  /** Backend-authored Highcharts defaults, merged beneath the builder's own options. */
  defaultOptions: Readonly<Record<string, unknown>> | null
  fieldRoles: readonly ChartFieldRole[]
  /** False means at least one role was malformed/unknown or role keys were duplicated. */
  fieldRolesValid: boolean
  /** Wire keys this row actually carried. An absent key falls back to the local renderer config;
   *  a present one — including an explicit `null` — is the backend's answer and wins. */
  suppliedFields: ReadonlySet<string>
}

/** Backend capability resolved onto one renderer that this frontend actually owns. */
export interface ChartTypeMeta extends Omit<
  VisualizationTypeDefinition,
  'kind' | 'type' | 'maxDimensions' | 'maxMetrics' | 'suppliedFields'
> {
  type: ChartType
  nameKey: `reports.chartTypes.${string}`
  /** Always finite once resolved onto an implemented renderer. */
  maxDimensions: number
  maxMetrics: number
}

export interface ChartAvailabilityInput {
  dimensions: readonly string[]
  metrics: readonly string[]
  timeDimensions?: readonly string[]
  allowedTypes?: readonly ChartType[]
  hasComparison?: boolean
  hasHierarchy?: boolean
  hasPairs?: boolean
  dataPointCount?: number
}

export interface ReportViewPreferenceV1 {
  version: 1
  view: ReportViewType
  chartType: ChartType
}

/** The resolved caps and defaults a builder honours; absent for direct (non-catalog) callers. */
export type ChartPresentation = Pick<
  ChartTypeMeta,
  'maxLimit' | 'maxSeries' | 'maxSmallMultiples' | 'defaultOptions'
>
